import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { getOfferingOrders } from '../ordersEndpoints'
import { ordersQueryKeys } from '../ordersQueryKeys'

/**
 * Every order placed on one offering, with each order's title-deed state.
 *
 * Published for the offerings module's detail drawer (architecture-principles
 * §10 — shared-endpoint hooks). It lives here because the endpoint hangs off the
 * orders controller and the rows are orders; the offering drawer is only where
 * an operator reads them.
 *
 * Gated on a truthy id AND on `enabled`, so the drawer can hold the request back
 * until the offering is actually in a state worth listing orders for — nothing
 * is fetched while the drawer is closed or the raise is still running.
 */
export function useOfferingOrdersQuery(
  offeringId: MaybeRefOrGetter<string | null | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  const idRef = computed(() => String(toValue(offeringId) ?? ''))
  return useQuery({
    queryKey: computed(() => ordersQueryKeys.offeringOrders(idRef.value)),
    queryFn: () => getOfferingOrders(idRef.value),
    enabled: computed(() => Boolean(idRef.value) && Boolean(toValue(enabled))),
  })
}
